import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailFlowApi } from '../services/api';
import { useAuthStore } from '../context/auth.store';
import toast from 'react-hot-toast';

const parseEmails = str => (str||'').split(';').map(e=>e.trim()).filter(Boolean);

const FREQ_STYLE = {
  daily:    { bg:'#EFF6FF', color:'#1D4ED8', icon:'🕐' },
  monthly:  { bg:'#F0FDF4', color:'#15803D', icon:'📅' },
  approval: { bg:'#FFF7ED', color:'#C2410C', icon:'✅' },
  trigger:  { bg:'#FFF7ED', color:'#C2410C', icon:'⚡' },
  default:  { bg:'#F8FAFC', color:'#475569', icon:'📋' },
};
const getFS = f => {
  const s=(f||'').toLowerCase();
  if (s.includes('daily'))   return FREQ_STYLE.daily;
  if (s.includes('monthly')) return FREQ_STYLE.monthly;
  if (s.includes('approval')||s.includes('upload')||s.includes('trigger')) return FREQ_STYLE.approval;
  return FREQ_STYLE.default;
};

function FlowCard({ flow, userEmail, onOpen }) {
  const intEmails = parseEmails(flow.internalEmails);
  const extEmails = parseEmails(flow.externalEmails);
  const fs = getFS(flow.frequency);
  const isInternal = intEmails.some(e=>e.toLowerCase()===userEmail?.toLowerCase());
  const hasOneDrive = !!(flow.onedriveFolderId || flow.onedriveFolderPath);

  return (
    <div style={{background:'white',border:'1px solid var(--border)',borderRadius:12,padding:16,borderTop:`3px solid ${fs.color}`,display:'flex',flexDirection:'column',gap:0}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10}}>
        <div style={{fontWeight:700,fontSize:14,color:'var(--text-primary)',lineHeight:1.4,flex:1,paddingRight:8}}>{flow.name}</div>
        <span style={{fontSize:10,padding:'3px 8px',borderRadius:99,background:isInternal?'#EFF6FF':'#FFF7ED',color:isInternal?'#1D4ED8':'#C2410C',fontWeight:700,flexShrink:0}}>
          {isInternal?'🏢 Internal':'🌐 External'}
        </span>
      </div>

      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
        <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:fs.bg,color:fs.color,fontWeight:700}}>{fs.icon} {flow.frequency||'—'}</span>
        {flow.sendTime&&<span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:'#F8FAFC',color:'var(--text-secondary)',fontWeight:500}}>⏰ {flow.sendTime}</span>}
        {hasOneDrive
          ? <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:'var(--success-bg)',color:'var(--success)',fontWeight:600}}>✓ OneDrive linked</span>
          : <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:'var(--warning-bg)',color:'var(--warning)',fontWeight:600}}>⚠ Folder not set</span>
        }
      </div>

      {flow.onedriveFolderPath&&(
        <div style={{fontSize:11,color:'var(--text-muted)',fontFamily:'monospace',marginBottom:10,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          📁 {flow.onedriveFolderPath}
        </div>
      )}

      {flow.notes&&(
        <div style={{fontSize:12,color:'#92400E',background:'#FFFBEB',borderRadius:6,padding:'6px 10px',marginBottom:10,lineHeight:1.5}}>
          📝 {flow.notes}
        </div>
      )}

      {/* Open report button */}
      <button
        className="btn btn-primary"
        style={{width:'100%',justifyContent:'center',marginTop:'auto',gap:6}}
        onClick={()=>onOpen(flow)}
        disabled={!hasOneDrive}
        title={!hasOneDrive?'Admin has not linked this report to OneDrive yet':'Open report'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        {hasOneDrive ? 'Open report' : 'Not yet available'}
      </button>
    </div>
  );
}

export default function MyEmailReportsPage() {
  const { user }   = useAuthStore();
  const navigate   = useNavigate();
  const [flows, setFlows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [freqFilter, setFreqFilter] = useState('');

  useEffect(()=>{
    if (!user?.email) return;
    emailFlowApi.byEmail(user.email)
      .then(res=>setFlows(res.data))
      .catch(()=>toast.error('Failed to load your report dispatches'))
      .finally(()=>setLoading(false));
  },[user?.email]);

  const visible = flows.filter(f=>{
    const matchSearch = !search || f.name.toLowerCase().includes(search.toLowerCase());
    const matchFreq   = !freqFilter || (f.frequency||'').toLowerCase().includes(freqFilter.toLowerCase());
    return matchSearch && matchFreq;
  });

  const freqs = [...new Set(flows.map(f=>f.frequency).filter(Boolean))].sort();
  const linked   = flows.filter(f=>f.onedriveFolderId||f.onedriveFolderPath).length;
  const internal = flows.filter(f=>parseEmails(f.internalEmails).some(e=>e.toLowerCase()===user?.email?.toLowerCase())).length;
  const external = flows.filter(f=>parseEmails(f.externalEmails).some(e=>e.toLowerCase()===user?.email?.toLowerCase())).length;

  const handleOpen = flow => navigate(`/flows/${flow.id}`);

  return (
    <>
      <div className="page-header">
        <div style={{paddingBottom:16}}>
          <div className="page-title">My Report Dispatches</div>
          <div className="page-subtitle">Reports dispatched to <strong>{user?.email}</strong></div>
        </div>
      </div>
      <div className="page-body">

        {!loading&&flows.length>0&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12,marginBottom:20}}>
            <div className="stat-card" style={{padding:'14px 16px'}}><div className="stat-label">Total</div><div className="stat-val" style={{fontSize:22,color:'var(--accent)'}}>{flows.length}</div></div>
            <div className="stat-card" style={{padding:'14px 16px'}}><div className="stat-label">Internal</div><div className="stat-val" style={{fontSize:22,color:'#1D4ED8'}}>{internal}</div></div>
            <div className="stat-card" style={{padding:'14px 16px'}}><div className="stat-label">External</div><div className="stat-val" style={{fontSize:22,color:'#C2410C'}}>{external}</div></div>
            <div className="stat-card" style={{padding:'14px 16px'}}><div className="stat-label">Available</div><div className="stat-val" style={{fontSize:22,color:'var(--success)'}}>{linked}</div></div>
          </div>
        )}

        <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
          <div className="search-box" style={{flex:1,minWidth:180}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input placeholder="Search reports…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          {freqs.length>1&&(
            <select className="form-input" style={{width:'auto'}} value={freqFilter} onChange={e=>setFreqFilter(e.target.value)}>
              <option value="">All frequencies</option>
              {freqs.map(f=><option key={f} value={f}>{f}</option>)}
            </select>
          )}
        </div>

        {loading&&<div style={{display:'flex',justifyContent:'center',padding:'48px 0'}}><div className="spinner"/></div>}

        {!loading&&flows.length===0&&(
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            <h3>No dispatches found</h3>
            <p>Your email ({user?.email}) is not assigned to any report flows yet.</p>
          </div>
        )}

        {!loading&&visible.length>0&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
            {visible.map(f=><FlowCard key={f.id} flow={f} userEmail={user?.email} onOpen={handleOpen}/>)}
          </div>
        )}

        {!loading&&flows.length>0&&visible.length===0&&(
          <div className="empty-state"><p>No flows match your filter.</p></div>
        )}
      </div>
    </>
  );
}
